/**
 * The three commands an agreement offers, each in its own confirmation.
 *
 * ===========================================================================
 * IDEMPOTENCY IS NOT DECORATION HERE
 * ===========================================================================
 * `send` and `execution` both refuse outright without an `Idempotency-Key`
 * (`_require_idempotency_key` in `domains/agreements/views.py`), because both have an
 * effect outside the system: a double send is a second copy of a contract in somebody's
 * inbox, and a double execution is an ambiguous signature trail.
 *
 * ONE KEY PER USER-INITIATED ATTEMPT, held in a ref so that pressing the button again
 * after a timeout replays the same attempt rather than starting a new one. A fresh key
 * per retry defeats the entire mechanism, and it is the quiet kind of bug: everything
 * looks fine until a slow network produces two of something.
 * ===========================================================================
 *
 * `countersign` deliberately carries no key. It is a pure state transition with no
 * external effect, it is a no-op when the agreement is already active, and the server
 * refuses it with a 409 until every required signatory has accepted.
 */

import {
  ConflictError,
  STAFF,
  newIdempotencyKey,
  qk,
  useApi,
  useApiMutation,
  type Agreement,
  type AgreementRecipient,
} from '@legalcrm/api';
import { Button, ConfirmModal, Modal, Select, useToast } from '@legalcrm/ui';
import { useRef, useState } from 'react';

import { formatDateTime } from '@/lib/format';

/** Every refusal on this resource is a 409 whose `detail` is written for the reader. */
function refusalMessage(error: unknown, fallback: string): string {
  return error instanceof ConflictError ? error.detail : fallback;
}

function invalidations(agreementId: string) {
  return [qk.all('agreements'), qk.detail('agreements', agreementId)];
}

// ---------------------------------------------------------------------------
// Send
// ---------------------------------------------------------------------------

export function SendAgreementDialog({
  agreement,
  onClose,
}: {
  agreement: Agreement;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  /** Minted once for this attempt; reused verbatim on every retry of it. */
  const key = useRef(newIdempotencyKey());

  const send = useApiMutation<Agreement, void>(
    {
      mutationFn: () =>
        api.post<Agreement>(STAFF.agreementSend(agreement.id), undefined, {
          idempotencyKey: key.current,
        }),
      invalidates: invalidations(agreement.id),
    },
    {
      onSuccess: () => {
        toast({
          title: 'Agreement issued',
          description: 'The text is now frozen. Later template edits cannot reach it.',
          tone: 'success',
        });
        onClose();
      },
      onError: (error) =>
        toast({
          title: 'Could not issue this agreement',
          description: refusalMessage(error, 'The agreement could not be issued. Try again.'),
          tone: 'warning',
        }),
    },
  );

  const required = agreement.recipients.filter((recipient) => recipient.is_required);

  return (
    <ConfirmModal
      open
      onClose={onClose}
      title="Issue this agreement for signature"
      description={agreement.title}
      confirmLabel="Issue it"
      loading={send.isPending}
      onConfirm={() => send.mutate()}
      consequences={
        <div className="flex flex-col gap-3 text-sm">
          <div className="rounded border border-warning/30 bg-warning-subtle px-3 py-2 text-ink">
            <strong className="font-semibold">This freezes the text.</strong> From now on the
            agreement carries its own copy of the wording. Editing the template afterwards
            will not change what these people were asked to sign — and once it is signed,
            nothing changes it at all.
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-subtle">
              Who must sign
            </p>
            {required.length === 0 ? (
              <p className="text-ink-muted">
                No required signatory is on this agreement yet. Recipients are fixed at
                issue, so add them before sending.
              </p>
            ) : (
              <ul className="flex flex-col gap-0.5 text-ink">
                {required.map((recipient) => (
                  <li key={recipient.id}>
                    {recipient.display_name}
                    {recipient.email && (
                      <span className="text-ink-muted"> — {recipient.email}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Resend one signatory's link
// ---------------------------------------------------------------------------

/**
 * Send this one person their link again.
 *
 * ===========================================================================
 * IT DOES NOT KILL THE LINK THEY ALREADY HAVE
 * ===========================================================================
 * Link tokens are stored hashed, so the original cannot be re-sent literally. The server
 * mints a second and revokes nothing: both open the same document as the same signatory,
 * so whichever one they find first works. Revoking the old one would break the email
 * somebody is reading at the moment the new one arrives — which is the exact opposite of
 * what this button is for, and would be invisible until they telephoned.
 *
 * Carries an `Idempotency-Key` for the same reason `send` does, and the key is held in a
 * ref so pressing again after a timeout replays the same attempt rather than putting a
 * second copy in somebody's inbox.
 *
 * Per signatory, never "resend to everybody". One person not having received theirs is
 * the ordinary case, and re-sending to the signatory who already signed is exactly the
 * message the server refuses.
 */
export function ResendLinkButton({
  agreement,
  recipient,
}: {
  agreement: Agreement;
  recipient: AgreementRecipient;
}) {
  const api = useApi();
  const { toast } = useToast();
  const key = useRef(newIdempotencyKey());

  const resend = useApiMutation<AgreementRecipient, void>(
    {
      mutationFn: () =>
        api.post<AgreementRecipient>(
          STAFF.agreementResend(agreement.id, recipient.id),
          undefined,
          { idempotencyKey: key.current },
        ),
      invalidates: [
        ...invalidations(agreement.id),
        qk.sub('agreements', agreement.id, 'status'),
      ],
    },
    {
      onSuccess: () => {
        toast({
          title: `Link sent again to ${recipient.display_name}`,
          description:
            'The link they already had still works — this is a second one, not a replacement.',
          tone: 'success',
        });
      },
      onError: (error) =>
        toast({
          title: 'Could not send it again',
          // The 409s here each name the thing to do instead: issue the agreement, or
          // record the signature of somebody with no address.
          description: refusalMessage(error, 'It could not be sent. Try again.'),
          tone: 'warning',
        }),
    },
  );

  const nowhereToSend = !recipient.email && !recipient.phone;

  return (
    <Button
      variant="ghost"
      size="sm"
      loading={resend.isPending}
      disabled={nowhereToSend}
      title={
        nowhereToSend
          ? 'No email address and no telephone number, so there is nowhere to send it. Recipients are fixed at issue — record their signature directly, or raise a new version.'
          : undefined
      }
      onClick={() => resend.mutate()}
    >
      Resend
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Countersign
// ---------------------------------------------------------------------------

export function CountersignDialog({
  agreement,
  outstanding,
  onClose,
}: {
  agreement: Agreement;
  /** Required signatories who have not yet accepted, from the status payload. */
  outstanding: AgreementRecipient[];
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();

  const countersign = useApiMutation<Agreement, void>(
    {
      mutationFn: () => api.post<Agreement>(STAFF.agreementCountersign(agreement.id)),
      invalidates: invalidations(agreement.id),
    },
    {
      onSuccess: () => {
        toast({
          title: 'Countersigned',
          description: 'The agreement is now active.',
          tone: 'success',
        });
        onClose();
      },
      onError: (error) =>
        toast({
          title: 'Countersignature refused',
          // 409 `not_fully_signed` names exactly what is missing. Verbatim.
          description: refusalMessage(error, 'The agreement could not be countersigned.'),
          tone: 'warning',
        }),
    },
  );

  return (
    <ConfirmModal
      open
      onClose={onClose}
      title="Countersign on behalf of the firm"
      description={agreement.title}
      confirmLabel="Countersign"
      loading={countersign.isPending}
      onConfirm={() => countersign.mutate()}
      consequences={
        outstanding.length > 0 ? (
          <div className="rounded border border-danger/30 bg-danger-subtle px-3 py-2 text-sm text-ink">
            <strong className="font-semibold">
              {outstanding.length === 1
                ? 'One required signatory has not accepted.'
                : `${outstanding.length} required signatories have not accepted.`}
            </strong>{' '}
            The server will refuse this: countersigning something the client has not
            signed produces a record asserting a bilateral agreement that does not exist.
            <ul className="mt-1.5 list-inside list-disc">
              {outstanding.map((recipient) => (
                <li key={recipient.id}>{recipient.display_name}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-ink-muted">
            Every required signatory has accepted. Countersigning records the firm&apos;s own
            signature and makes the agreement active.
          </p>
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Record an execution
// ---------------------------------------------------------------------------

const METHODS = [
  { value: 'click_accept', label: 'Accepted in person, on screen' },
  { value: 'drawn', label: 'Signature drawn' },
  { value: 'provider', label: 'Through a signature provider' },
];

/**
 * Somebody signed in the office, or posted back a wet-signed copy.
 *
 * The recipient is CHOSEN, never inferred. Recording an execution against "whoever is
 * next" is how a joint client's agreement ends up attributed to the wrong spouse, and
 * an evidence package is worth nothing if the name on it is a guess — which is why the
 * server requires the field even when there is only one recipient.
 */
export function RecordExecutionDialog({
  agreement,
  onClose,
}: {
  agreement: Agreement;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const key = useRef(newIdempotencyKey());

  const pending = agreement.recipients.filter((recipient) => recipient.status !== 'accepted');
  const [recipientId, setRecipientId] = useState(pending[0]?.id ?? '');
  const [method, setMethod] = useState('click_accept');

  const execute = useApiMutation<Agreement, { recipient: string; method: string }>(
    {
      mutationFn: (body) =>
        api.post<Agreement>(STAFF.agreementExecution(agreement.id), body, {
          idempotencyKey: key.current,
        }),
      invalidates: invalidations(agreement.id),
    },
    {
      onSuccess: () => {
        toast({
          title: 'Execution recorded',
          description: 'The signature evidence has been added to the record.',
          tone: 'success',
        });
        onClose();
      },
      onError: (error) =>
        toast({
          title: 'Could not record the execution',
          description: refusalMessage(error, 'It could not be recorded. Try again.'),
          tone: 'warning',
        }),
    },
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Record a signature"
      description="For a signatory who signed in the office or returned a signed copy."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={execute.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!recipientId}
            loading={execute.isPending}
            onClick={() => execute.mutate({ recipient: recipientId, method })}
          >
            Record it
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {pending.length === 0 ? (
          <p className="text-sm text-ink-muted">
            Every signatory on this agreement has already accepted.
          </p>
        ) : (
          <Select
            label="Who signed"
            hint="Named, never inferred. An evidence package with a guessed name is worth nothing."
            required
            options={pending.map((recipient) => ({
              value: recipient.id,
              label: recipient.email
                ? `${recipient.display_name} (${recipient.email})`
                : recipient.display_name,
            }))}
            value={recipientId}
            onChange={(event) => setRecipientId(event.target.value)}
          />
        )}

        <Select
          label="How they signed"
          options={METHODS}
          value={method}
          onChange={(event) => setMethod(event.target.value)}
        />

        <div className="rounded border bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
          This records the same evidence package a click-accept produces — the checksum of
          the exact text, the moment, and who it is attributed to. Recording it twice is
          safe: the server treats a repeat as the same acceptance rather than a second
          signature.
          {agreement.sent_at && (
            <span className="mt-1 block">Issued {formatDateTime(agreement.sent_at)}.</span>
          )}
        </div>
      </div>
    </Modal>
  );
}
